// src/pages/Queue.jsx — navbatga turish (production flow)
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/Navbar';
import { Card, Badge, Button, Spinner, StatusTimeline } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { queueApi, orderApi } from '../services/api';
import { useFleetStatus } from '../hooks/useFleetStatus';
import { useOrderSocket } from '../hooks/useOrderSocket';
import {
  ORDER_STATUS,
  ORDER_STATUS_STEP,
  ORDER_STATUS_MAP,
  GAS_UNIT_PRICE,
  FLEET_STATUS_MESSAGE,
} from '../constants';

export default function QueuePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { fleet, loading: fleetLoading, refresh: refreshFleet } = useFleetStatus(10000);
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [isPriority, setIsPriority] = useState(false);
  const [quantity, setQuantity] = useState(1);

  useOrderSocket({ onGeofence: () => refreshFleet(), onRefresh: () => refreshAll() });

  const QUEUE_STEPS = useMemo(() => [
    { label: t('queuePage.steps.created.label'), sub: t('queuePage.steps.created.sub') },
    { label: t('queuePage.steps.confirmed.label'), sub: t('queuePage.steps.confirmed.sub') },
    { label: t('queuePage.steps.preparing.label'), sub: t('queuePage.steps.preparing.sub') },
    { label: t('queuePage.steps.onWay.label'), sub: t('queuePage.steps.onWay.sub') },
    { label: t('queuePage.steps.delivered.label'), sub: t('queuePage.steps.delivered.sub') },
  ], [t]);

  const loadQueue = useCallback(async () => {
    if (!fleet?.activeRoute?.id && !fleet?.canJoinQueue) {
      setQueue([]);
      setQueueLoading(false);
      return;
    }
    try {
      const data = await queueApi.getAll('active');
      setQueue(Array.isArray(data) ? data : []);
    } catch {
      setQueue([]);
    } finally {
      setQueueLoading(false);
    }
  }, [fleet?.activeRoute?.id, fleet?.canJoinQueue]);

  const refreshAll = useCallback(async () => {
    await refreshFleet();
    await loadQueue();
  }, [refreshFleet, loadQueue]);

  useEffect(() => {
    if (!fleetLoading) loadQueue();
  }, [fleetLoading, fleet, loadQueue]);

  const userOrder = fleet?.userOrder;
  const inQueue = userOrder?.status === ORDER_STATUS.PENDING;
  const myPosition = fleet?.queuePosition?.position ?? 0;
  const estimatedMin = fleet?.queuePosition?.estimatedWaitTimeMinutes ?? Math.max(0, (myPosition - 1) * 5);
  const unitPrice = fleet?.unitPrice ?? GAS_UNIT_PRICE;
  const totalPrice = quantity * (isPriority ? unitPrice * 1.2 : unitPrice);
  const loading = fleetLoading || queueLoading;

  const currentStep = userOrder
    ? (ORDER_STATUS_STEP[userOrder.status] ?? 0)
    : 0;

  const fleetHint = fleet?.message ? (FLEET_STATUS_MESSAGE[fleet.message] || fleet.message) : null;

  const join = async () => {
    if (!fleet?.canJoinQueue) {
      if (fleet?.message === 'NO_MAHALLA') {
        toast.error(FLEET_STATUS_MESSAGE.NO_MAHALLA);
        navigate('/profile');
        return;
      }
      toast.error(fleetHint || t('dashboard.noActiveFleet'));
      return;
    }
    setJoining(true);
    try {
      await orderApi.create({
        routeId: 'active',
        quantity,
        price: unitPrice,
        isPriority,
      });
      toast.success(t('queuePage.messages.joined'));
      await refreshAll();
    } catch (err) {
      if (err.message === 'INSUFFICIENT_FUNDS') {
        toast.error('Balans yetarli emas. Hisobni to\'ldiring.');
        navigate('/tolov');
      } else if (err.message === 'ALREADY_IN_QUEUE') {
        toast.info('Siz allaqachon navbatdasiz');
        await refreshAll();
      } else if (err.message === 'NO_MAHALLA') {
        toast.error(FLEET_STATUS_MESSAGE.NO_MAHALLA);
        navigate('/profile');
      } else {
        toast.error(err.message || t('apiErrors.INTERNAL_ERROR'));
      }
    } finally {
      setJoining(false);
    }
  };

  const leave = async () => {
    if (!userOrder?.id) return;
    setLeaving(true);
    try {
      await queueApi.leave(userOrder.id);
      toast.success(t('queuePage.messages.left'));
      await refreshAll();
    } catch (err) {
      toast.error(err.message || t('apiErrors.INTERNAL_ERROR'));
    } finally {
      setLeaving(false);
    }
  };

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto py-4">
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
            {t('queuePage.title')}
          </h1>
          <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-1 opacity-70">
            {t('queuePage.subtitle')}
          </p>
        </motion.div>

        {fleet?.activeRoute && (
          <Card className="p-5 mb-6 rounded-3xl border-emerald-100 bg-emerald-50/50 dark:bg-emerald-900/10">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-[#1e9f6b]">local_shipping</span>
              <div className="flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t('mainPage.activeDelivery')}</p>
                <p className="text-sm font-black text-slate-900 dark:text-white italic">
                  {t('queuePage.routeInfo', { status: fleet.activeRoute.status, count: fleet.activeRoute.mahallaIds?.length || 0 })}
                </p>
              </div>
              <Badge variant="green" pulse>{t('common.live')}</Badge>
            </div>
          </Card>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8">
          <AnimatePresence mode="wait">
            {loading ? (
              <Card key="loading" className="p-12 flex justify-center rounded-[40px]">
                <Spinner size="md" />
              </Card>
            ) : inQueue ? (
              <motion.div key="in-queue" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
                <div className="bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] rounded-[40px] p-10 relative overflow-hidden shadow-2xl shadow-[#1e9f6b]/30">
                  <div className="relative z-10">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-8 mb-10">
                      <div>
                        <p className="text-white/60 text-[10px] font-black uppercase tracking-[0.2em] mb-3 italic">
                          {t('queuePage.yourPosition')}
                        </p>
                        <div className="flex items-center gap-4">
                          <span className="text-7xl font-black text-white italic">#{myPosition}</span>
                          <Badge variant="green" pulse className="bg-white/10 text-white border-0 font-black uppercase text-[10px]">
                            {ORDER_STATUS_MAP[userOrder.status]?.label || userOrder.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="sm:text-right">
                        <p className="text-white/60 text-[10px] font-black uppercase tracking-[0.2em] mb-3 italic">
                          {t('queuePage.estimatedTime')}
                        </p>
                        <span className="text-4xl font-black text-white italic">~{estimatedMin}</span>
                        <span className="text-white/60 text-[10px] font-black uppercase ml-1">{t('queuePage.minutes')}</span>
                      </div>
                    </div>
                    {userOrder.qrCodeToken && (
                      <p className="text-white/70 text-xs font-mono mb-6">QR: {userOrder.qrCodeToken}</p>
                    )}
                    <button
                      type="button"
                      onClick={leave}
                      disabled={leaving}
                      className="w-full h-14 rounded-2xl bg-white/10 hover:bg-red-500 text-white font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2"
                    >
                      {leaving ? <Spinner size="xs" color="white" /> : t('queuePage.leave')}
                    </button>
                  </div>
                </div>
                <Card className="p-8 mt-6 rounded-[40px]">
                  <StatusTimeline steps={QUEUE_STEPS} currentStep={Math.min(currentStep, QUEUE_STEPS.length - 1)} />
                </Card>
              </motion.div>
            ) : (
              <motion.div key="join" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
                <Card className="p-10 flex flex-col gap-6 rounded-[40px]">
                  {fleetHint && (
                    <p className="text-center text-[10px] font-bold text-amber-600 uppercase tracking-widest">{fleetHint}</p>
                  )}
                  <div className="flex gap-4 justify-center">
                    {[1, 2, 3].map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setQuantity(q)}
                        className={`h-16 w-16 rounded-2xl font-black text-xl transition-all duration-150 ${
                          quantity === q
                            ? 'bg-[#1e9f6b] text-white shadow-lg shadow-[#1e9f6b]/30 scale-105'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={isPriority} onChange={(e) => setIsPriority(e.target.checked)} className="rounded" />
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                      Ustuvor navbat (+20%) — {Math.round(unitPrice * 1.2).toLocaleString()} UZS
                    </span>
                  </label>
                  <p className="text-center text-sm font-black text-slate-500">
                    Jami: <span className="text-[#1e9f6b]">{Math.round(totalPrice).toLocaleString()} UZS</span>
                    <span className="block text-[10px] mt-1 opacity-70">Balans: {(user?.balance ?? 0).toLocaleString()} UZS</span>
                  </p>
                  <Button
                    onClick={join}
                    loading={joining}
                    disabled={!fleet?.canJoinQueue}
                    size="xl"
                    className={`h-16 font-black uppercase tracking-widest ${
                      !fleet?.canJoinQueue ? '!bg-slate-300 dark:!bg-slate-700 !text-slate-500 cursor-not-allowed opacity-90' : ''
                    }`}
                  >
                    {fleet?.canJoinQueue ? t('queuePage.join') : (fleetHint || t('dashboard.noActiveFleet'))}
                  </Button>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <Card className="overflow-hidden rounded-[40px]">
          <div className="px-8 py-6 border-b flex justify-between items-center">
            <h2 className="text-xs font-black uppercase tracking-widest">{t('queuePage.listTitle')}</h2>
            <button type="button" onClick={refreshAll} className="material-symbols-outlined text-slate-400 hover:text-[#1e9f6b]">
              refresh
            </button>
          </div>
          {queue.length === 0 ? (
            <p className="py-16 text-center text-[10px] font-black uppercase text-slate-400">{t('queuePage.listEmpty')}</p>
          ) : (
            <div className="divide-y">
              {queue.map((entry) => {
                const isMe = entry.userId === user?.id || entry.user?.id === user?.id;
                return (
                  <div
                    key={entry.id}
                    className={`flex items-center gap-4 px-8 py-5 ${isMe ? 'bg-emerald-50/50' : ''}`}
                  >
                    <div className="h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center font-black text-lg">
                      {entry.position}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-sm truncate">{entry.user?.name || t('common.user')}</p>
                      <p className="text-[10px] text-slate-400 uppercase">
                        {entry.isPriority ? 'Ustuvor' : ''} · VIP {entry.user?.priorityLevel ?? 3}
                      </p>
                    </div>
                    {entry.position === 1 && <Badge variant="amber">{t('queuePage.next')}</Badge>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
